import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
// @ts-ignore
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import FacebookSettingsModal from '@alltrails/shared/components/FacebookSettingsModal';
import PrimaryButtonLarge from '@alltrails/shared/components/PrimaryButtonLarge';
import SecondaryButtonLarge from '@alltrails/shared/components/SecondaryButtonLarge';
import FacebookLoginButton from '../forms/buttons/FacebookLoginButton';
import { StringConstantsMixin } from '../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { ServerCommunicationUtil } from '../../utils/server_communication_util';
import useInterval from '../../hooks/useInterval';
import { uploadFacebookTokenToBelay, getFacebookUploadStatus, stopSyncFacebookFriends } from '@alltrails/communityapp/belayRequests';
import { FacebookStatus } from '@alltrails/communityapp/constants';
import { facebookHandler } from 'utils/OAuthHandlers';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';

export type FacebookSettingsProps = {
  userId: number;
  facebookAppId: string;
  userFacebookId: string | null;
  unlinkPath: string;
};

const {
  strings: {
    FACEBOOK: { LOGIN_FACEBOOK, FACEBOOK_SETTINGS, SYNC, REFRESH_FRIENDS }
  }
} = StringConstantsMixin;

const renderFacebookButton = (isLogin: boolean, isSynced: boolean, isSyncing: boolean, renderProps: { onClick: () => void }) => {
  if (isLogin) {
    return <FacebookLoginButton text={LOGIN_FACEBOOK} handler={renderProps.onClick} />;
  }
  if (isSynced) {
    return (
      <SecondaryButtonLarge name={REFRESH_FRIENDS} onClick={renderProps.onClick} disabled={isSyncing}>
        {REFRESH_FRIENDS}
      </SecondaryButtonLarge>
    );
  }
  return (
    <PrimaryButtonLarge name={SYNC} onClick={renderProps.onClick} disabled={isSyncing}>
      {SYNC}
    </PrimaryButtonLarge>
  );
};

const FacebookSettings = ({ userId, facebookAppId, userFacebookId, unlinkPath }: FacebookSettingsProps) => {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [delay, setDelay] = useState<number | null>(2000);
  const [facebookStatus, setFacebookStatus] = useState(FacebookStatus.NotSubmitted);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSynced, setIsSynced] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const languageRegionCode = useLanguageRegionCode();

  const closeModal = () => {
    setShowSettingsModal(false);
  };

  const handleUnlinkClick = () => {
    _gaq.push(['_trackEvent', 'Account Settings', 'Facebook', 'Unlink']);
    ServerCommunicationUtil.postApiEndpoint(
      unlinkPath,
      {},
      () => setIsLoggedIn(false),
      () => console.error('error when unlinking facebook'),
      () => {}
    );
  };

  const facebookLoginHandler = (response: { accessToken: string; userID: string; expiresIn: number }) => {
    if (!response.accessToken) {
      console.error('handleFacebookResponeLogin', response);
      setIsLoggedIn(false);
      return;
    }

    const resp = {
      accessToken: response.accessToken,
      userID: response.userID,
      expiresIn: response.expiresIn
    };

    facebookHandler(
      resp,
      languageRegionCode,
      () => {},
      () => {},
      () => {}
    );
    setIsLoggedIn(true);
  };

  const FacebookButton = (): JSX.Element => {
    return (
      <FacebookLogin
        disableMobileRedirect
        appId={facebookAppId}
        autoLoad
        callback={facebookLoginHandler}
        scope="email,user_friends,user_gender,user_birthday,user_location"
        render={(renderProps: { onClick: () => void }) => renderFacebookButton(true, isSynced, isSyncing, renderProps)}
      />
    );
  };

  useEffect(() => {
    if (userId) {
      const fetchFacebookStatus = async () => {
        const { data } = await getFacebookUploadStatus(userId);
        setFacebookStatus(data.contactUploads.facebook.status);
      };

      fetchFacebookStatus().catch(console.error);
    }
  }, []);

  useEffect(() => {
    if (facebookStatus === FacebookStatus.Done) {
      setIsLoggedIn(true);
      setIsSynced(true);
    } else {
      setIsLoggedIn(false);
      setIsSynced(false);
    }
  }, [facebookStatus]);

  const getFacebookStatusPoll = async () => {
    if (accessToken && userId) {
      const { data } = await getFacebookUploadStatus(userId);
      setFacebookStatus(data.contactUploads.facebook.status);

      if (facebookStatus === FacebookStatus.Failed) {
        setIsSyncing(false);
        setDelay(null);
      }
      if (facebookStatus === FacebookStatus.Done) {
        setIsSyncing(false);
        setIsSynced(true);
        setDelay(null);
      }
    }
  };

  useInterval(() => {
    getFacebookStatusPoll();
  }, delay);

  const stopSyncFriends = async () => {
    if (userId) {
      try {
        await stopSyncFacebookFriends(userId);
        setIsSynced(false);
      } catch (err) {
        console.error('error when stop sync friends', err);
        setIsSynced(true);
      }
    }
  };

  const syncFacebookFriends = async (response: { accessToken: string }) => {
    if (!response.accessToken) {
      console.error('handleFacebookResponseSyncFriends', response);
      return;
    }

    setIsSyncing(true);
    setAccessToken(response.accessToken);
    try {
      await uploadFacebookTokenToBelay(response.accessToken, userId);
      setIsSyncing(false);
      setIsSynced(true);
    } catch (err) {
      console.error('error when syncing friends', err);
      setIsSynced(true);
    }
  };

  const FacebookSyncFriendsButton = (): JSX.Element => {
    return (
      <FacebookLogin
        disableMobileRedirect
        appId={facebookAppId}
        autoLoad
        callback={syncFacebookFriends}
        scope="user_friends"
        render={(renderProps: { onClick: () => void }) => renderFacebookButton(false, isSynced, isSyncing, renderProps)}
      />
    );
  };

  return (
    <>
      {showSettingsModal &&
        createPortal(
          <FacebookSettingsModal
            closeModal={closeModal}
            facebookLoginButton={<FacebookButton />}
            isLoggedIn={isLoggedIn || !!userFacebookId}
            handleDisconnect={handleUnlinkClick}
            facebookSyncFriendsButton={<FacebookSyncFriendsButton />}
            isSynced={isSynced}
            handleUnsync={stopSyncFriends}
          />,
          document.getElementById('modalPortal')
        )}
      <div>
        <FacebookLoginButton text={FACEBOOK_SETTINGS} handler={() => setShowSettingsModal(true)} />
      </div>
    </>
  );
};

export default FacebookSettings;
